import {
  type AuthServiceInterface,
  type AxiosInstance,
  type Tokens,
} from '@mint-lib/api';
import { EventEmitter } from '@mint-lib/service-locator';

import { BaseAuthService, type BaseAuthEvents } from './BaseAuthService.js';
import type { WorkspacesAuthStorage } from './types.js';

export type AuthEvents = BaseAuthEvents & {
  workspaceAuth: [];
  workspaceLogout: [];
};

export type Auth0Credentials = {
  domain: string;
  applicationClientId: string;
};

export class AuthService
  extends BaseAuthService<WorkspacesAuthStorage>
  implements AuthServiceInterface
{
  private accessApi: AxiosInstance | null = null;
  auth0Credentials: Auth0Credentials | null = null;

  constructor(
    guestApi: AxiosInstance,
    storage: WorkspacesAuthStorage,
    auth0Credentials?: Auth0Credentials,
  ) {
    super(guestApi, storage);
    this.auth0Credentials = auth0Credentials ?? null;
  }

  protected readonly eventEmitter: EventEmitter<AuthEvents> =
    new EventEmitter();

  override async provideBearerToken(useWorkspace?: boolean): Promise<string> {
    await this.ensureTokensValid();
    return `jwt ${this.storage.getAccessToken()}`;
  }

  setAccessApi(accessApi: AxiosInstance) {
    this.accessApi = accessApi;
  }

  workspaceRefreshTokenExpired() {
    return !this.isTokenAlive(this.storage.getWorkspaceRefreshTokenExpiresIn());
  }

  workspaceAccessTokenExpired() {
    return !this.isTokenAlive(this.storage.getAccessTokenExpiresIn() - 60);
  }

  saveWorkspaceTokens({
    access,
    refresh,
    accessExpiresIn,
    refreshExpiresIn,
  }: Tokens) {
    this.storage.setWorkspaceAccessToken(access);
    this.storage.setWorkspaceRefreshToken(refresh);
    this.storage.setWorkspaceAccessTokenExpiresIn(accessExpiresIn);
    this.storage.setWorkspaceRefreshTokenExpiresIn(refreshExpiresIn);
    return this.eventEmitter.emit('workspaceAuth');
  }

  clearWorkspaceSession() {
    this.storage.setWorkspaceAccessToken(null);
    this.storage.setWorkspaceRefreshToken(null);
    this.storage.setWorkspaceAccessTokenExpiresIn(null);
    this.storage.setWorkspaceRefreshTokenExpiresIn(null);
    return this.eventEmitter.emit('workspaceLogout');
  }
}
